import { Box, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import { ProfileEmployeeCustomFieldOptionDataFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import MoneyField from 'modules/core/components/Inputs/MoneyField'
import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import type MultiSelect from 'modules/core/components/MultiSelect'
import Rating from 'modules/core/components/Rating'
import createComputed from 'modules/core/hooks/createComputed'
import i18n from 'modules/core/lib/i18n'
import validateNumberInput from 'modules/core/lib/validateNumberInput'

type MultiSelectProps = React.ComponentProps<typeof MultiSelect>

type ChangeFunction = () => void
interface OptionalParams {
  onChange?: ChangeFunction
  readonly?: boolean
}
interface Props {
  form: Form
  optional?: OptionalParams
  currency?: string
}

const useSingleChoiceOptions = createComputed((form: Form) => {
  const options = form.has('options') ? form.get('options').value ?? [] : []
  return options
    .filter(
      (option: ProfileEmployeeCustomFieldOptionDataFragment) =>
        option.isActive || form.get('value').value === option.value
    )
    .map((option: ProfileEmployeeCustomFieldOptionDataFragment) => ({
      value: option.value,
      label: option.label,
      disabled: !option?.isActive,
    }))
})

export const InputItem = observer(({ form, currency, optional = {} }: Props) => {
  const { onChange, readonly } = optional
  const onChangeCallback = onChange || (() => {})
  const singleChoiceOptions = useSingleChoiceOptions(form)

  const onChangeToggle = () => {
    const field = form.get('value')
    field.set(!field.value)
  }

  const onChangeRating = (value: number) => {
    form.get('value').set(value)
  }

  const isReadonly = readonly || !form.get('editable').value

  const updateValue: MultiSelectProps['onSubmit'] = (updatedOptions) => {
    const field = form.get('value')
    field.value.length = 0 // emptying field value

    updatedOptions.forEach((option) => {
      if (option.selected) field.value.push(option.value)
    })
    field.set(field.value)
  }

  switch (form.get('field_type').value) {
    case 'text':
      return (
        <TextInput
          mandatory={form.get('mandatory').value}
          readonly={isReadonly}
          label={form.get('name').value}
          field={form.get('value')}
          onChange={onChangeCallback}
        />
      )

    case 'money':
      return (
        <MoneyField
          mandatory={form.get('mandatory').value}
          readonly={isReadonly}
          label={form.get('name').value}
          field={form.get('value')}
          onChange={onChangeCallback}
          currency={currency ?? ''}
        />
      )

    case 'cents': {
      const mandatory = form.get('mandatory').value

      return (
        <TextInput
          mandatory={mandatory}
          readonly={isReadonly}
          label={form.get('name').value}
          field={form.get('value')}
          onChange={onChangeCallback}
          validation={(value) => validateNumberInput(value, !mandatory)}
        />
      )
    }

    case 'date': {
      const mandatory = form.get('mandatory').value

      const withNullLabel = form.has('withNullLabel') ? form.get('withNullLabel').value : undefined

      const withNull = !mandatory && withNullLabel

      return (
        <DayPicker
          mandatory={mandatory}
          readonly={isReadonly}
          withNull={withNull}
          withNullLabel={withNullLabel}
          label={form.get('name').value}
          field={form.get('value')}
          placeholder={i18n.t('common.not_applicable')}
        />
      )
    }

    case 'long_text':
      return (
        <TextArea
          mandatory={form.get('mandatory').value}
          readonly={isReadonly}
          label={form.get('name').value}
          field={form.get('value')}
          onChange={onChangeCallback}
        />
      )

    case 'checkbox':
      return (
        <TogglerCheckbox
          value={!!form.get('value').value}
          disabled={!form.get('editable').value}
          label={form.get('name').value}
          onChange={onChangeToggle}
        />
      )

    case 'single_choice':
    case 'multiple_choice':
      return (
        <Select
          mandatory={form.get('mandatory').value}
          none={i18n.t('settings.custom_fields.new.labels.choose')}
          readonly={isReadonly}
          label={form.get('name').value}
          field={form.get('value')}
          options={singleChoiceOptions.get()}
          showOriginalLabelText
        />
      )

    case 'rating':
      return (
        <Box gap="s4">
          <Text.Normal size="200" weight="medium" color="grey800">
            {form.get('name').value}
          </Text.Normal>
          <Rating
            max={Number(form.get('max_value').value)}
            min={Number(form.get('min_value').value)}
            onClick={onChangeRating}
            readonly={readonly}
            value={+form.get('value').value}
          />
        </Box>
      )

    default:
      return null
  }
})
